import { Suspense, lazy, useContext, useEffect, useMemo, useState } from 'react';
import { Outlet } from 'react-router-dom';import { I18nProvider } from '../app/i18n/i18nProvider';
import { LayoutSplashScreen } from '../app/layout/core/OpenSooqSplashScreen';
import { NotificationProvider } from 'opensooq-notification';
import { useLang } from '../app/i18n/OpenSooqi18n';

import { getCookies, showChatInCountry } from './helpers/commonFunctions';
import { isMobileSource } from 'newApp/helpers/device';
import { resetSharedTokenPromise } from 'newApp/helpers/auth';
import AuthProvider, { AuthContext } from './i18n/AuthProvider';
import { useLogging } from '../logging/hooks/useLogging';
import { RTEventResponseType } from './interfaces/RealTimeModal';
import { fetchAccountOverview } from 'newApp/hooks/useAccountOverview';
import useAccountOwnerStore from './layout/components/sidebar/sidebar-menu/AccountOwnerStore';
import { SocketContexWtProvider } from './modules/socket-v5/providers/Socket';
import Api from '../api/api';
import { NotificationData, NotificationResponse } from './types';
import ConfirmationProvider from 'newApp/context/confirmation/ConfirmationProvider';

// Lazy: this modal only opens on a real-time event, but importing it eagerly put
// react-modal-sheet and framer-motion (~149 kB) in the entry chunk.
const RealTimeModal = lazy(() => import('./modales/RealTimeModal'));
// Lazy: only rendered for a new-device login alert, and it pulls react-bootstrap and
// react-toastify with it.
const SecurityAlert = lazy(() => import('./components/SecurityAlert'));

const App = () => {
  const [realTimeModal, setRealTimeModal] = useState(false);
  const [data, setData] = useState<RTEventResponseType>();
  const [securityAlertOpen, setSecurityAlertOpen] = useState(false);
  const [securityAlertData, setSecurityAlertData] = useState<NotificationData | null>(null);
  const { setAccountOwner, setWalletLabels } = useAccountOwnerStore();
  const lang = useLang();
  useLogging();

  const FetchProfile = async () => {
    try {
      const overview = await fetchAccountOverview();
      setAccountOwner(overview.member_owner ?? null);
      setWalletLabels(overview.wallet ?? null);
    } catch (error) {}
  };

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    resetSharedTokenPromise();
    FetchProfile();
    if (isMobileSource()) {
      document.body.classList.add('mobileSite');
    } else {
      document.body.classList.add('desktopSite');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const RTHandler = (data: RTEventResponseType) => {
    setData(data);
    setRealTimeModal(true);
  };

  // Was called in the render body, so it re-ran on every render. Loaded on demand
  // because this is the only module that imports Sentry.
  useEffect(() => {
    const dsn = process.env.REACT_APP_SENTRY_URL;

    if (!dsn) return;

    void import('@sentry/react').then((Sentry) => {
      Sentry.init({
        dsn,
        integrations: [new Sentry.BrowserTracing()],
        tracesSampleRate: 1.0,
      });
    });
  }, []);

  // Imported on demand: the Firebase messaging SDK is only needed once we know
  // notifications are permitted, and a static import also ran initializeApp() at
  // module scope during startup.
  useEffect(() => {
    if (!('Notification' in window) || Notification.permission === 'denied') return;

    void import('./firebase/firebaseConfig').then(
      ({ registerServiceWorker, requestNotificationPermission, onForegroundMessage }) => {
        registerServiceWorker();

        requestNotificationPermission().then((token) => {
          if (token) {
            Api.getInstance().request.post(
              'firebases/register',
              {
                GulpinRegister: { token: decodeURIComponent(token), channel: 'firebase' },
              },
              'firebases/register',
            );
          }
        });

        onForegroundMessage((payload: NotificationResponse) => {
          if (payload?.data?.notification_name === 'new_device_login_alert') {
            setSecurityAlertData(payload.data);
            setSecurityAlertOpen(true);
          }
        });
      },
    );
  }, []);

  return (
    <Suspense fallback={<LayoutSplashScreen />}>
      <AuthProvider>
        <SocketContexWtProvider>
          <I18nProvider>
            <ConfirmationProvider>
              {showChatInCountry() && (
                <Bundles RTHandler={RTHandler}>
                  <Outlet />
                </Bundles>
              )}
              {!showChatInCountry() && (
                <BundlesNoChat RTHandler={RTHandler}>
                  <Outlet />
                </BundlesNoChat>
              )}
              {securityAlertOpen && securityAlertData && (
                <Suspense fallback={null}>
                  <SecurityAlert
                    isOpen={securityAlertOpen}
                    onClose={() => setSecurityAlertOpen(false)}
                    notificationData={securityAlertData}
                  />
                </Suspense>
              )}
                {data && (
                  <Suspense fallback={null}>
                    <RealTimeModal
                      RTResponse={data}
                      show={realTimeModal}
                      setRealTimeModal={setRealTimeModal}
                    />
                  </Suspense>
                )}
            </ConfirmationProvider>
          </I18nProvider>
        </SocketContexWtProvider>
      </AuthProvider>
    </Suspense>
  );
};

const Bundles = ({
  children,
  RTHandler,
}: {
  children: any;
  RTHandler: (data: RTEventResponseType) => void;
}) => {
  const cookies = getCookies();
  const lang = useLang();
  const { isLoggedIn, regenerateToken, userIdentity } = useContext(AuthContext);
  let authenticationData = {
    userInfo: cookies.userInfo,
    source: cookies.source,
    audience: cookies.audience,
    country: cookies.ecountry,
    shopName: '',
  };
  const BundlesMemoed = useMemo(() => {
    if (userIdentity && userIdentity.shopName) {
      authenticationData.userInfo.shopName = userIdentity.shopName;
    }

    return (
      <NotificationProvider
        RTHandler={RTHandler}
        authenticationData={authenticationData}
        urls={{ gulpin2: process.env.REACT_APP_GULPIN_2_URL ?? '' }}
        lang={lang}
        publicUrl={window.location.origin}
        getToken={regenerateToken}
      >
        {children}
      </NotificationProvider>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, userIdentity]);
  return BundlesMemoed;
};

const BundlesNoChat = ({
  children,
  RTHandler,
}: {
  children: any;
  RTHandler: (data: RTEventResponseType) => void;
}) => {
  const cookies = getCookies();
  const lang = useLang();
  const authenticationData = {
    userInfo: cookies.userInfo,
    source: cookies.source,
    audience: cookies.audience,
    country: cookies.ecountry,
  };
  const { isLoggedIn, regenerateToken } = useContext(AuthContext);
  const BundlesMemoed = useMemo(() => {
    return (
      <NotificationProvider
        RTHandler={RTHandler}
        authenticationData={authenticationData}
        urls={{ gulpin2: process.env.REACT_APP_GULPIN_2_URL ?? '' }}
        lang={lang}
        publicUrl={window.location.origin}
        getToken={regenerateToken}
      >
        {children}
      </NotificationProvider>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);
  return BundlesMemoed;
};
export { App };
